/**
 * Fábrica que cria um handler para `columnsBlock` a partir do mapa de handlers.
 * O handler retornado usará os `handlers` fornecidos para processar tipos de
 * blocos internos, aplicando recursão para estruturas aninhadas.
 *
 * @param {Object} handlers - mapa de tipo -> função handler (ex: { 'listing': listingHandler })
 * @returns {Function} handler para blocos do tipo `columnsBlock`
 */
const createColumnsHandler = (handlers) => {
  if (!handlers || typeof handlers !== 'object') {
    throw new TypeError('handlers deve ser um objeto não nulo');
  }

  /**
   * Handler para blocos do tipo `columnsBlock`.
   * Acessa diretamente `data.blocks` ou `blocks` e passa para `traverseMap`,
   * evitando que o próprio bloco seja reprocessado e cause recursão infinita.
   *
   * Não usa o padrão `{ root: block }` pois `traverseMap` identificaria o
   * `columnsBlock` pelo `@type` e chamaria este handler novamente infinitamente.
   *
   * @param {Object} block - bloco do tipo `columnsBlock`
   * @param {*} content - contexto repassado para os handlers
   * @returns {Object} bloco com filhos processados
   */
  function columnsHandler(block, content) {
    if (!block) return block;

    // `data.blocks` é o formato padrão do columnsBlock no Volto.
    // `blocks` é o formato alternativo usado em algumas variações.
    if (block.data?.blocks) {
      const processedBlocks = traverseMap(block.data.blocks, content);
      if (processedBlocks === block.data.blocks) {
        return block;
      }

      return {
        ...block,
        data: {
          ...block.data,
          blocks: processedBlocks,
        },
      };
    } else if (block.blocks) {
      const processedBlocks = traverseMap(block.blocks, content);
      if (processedBlocks === block.blocks) {
        return block;
      }

      return {
        ...block,
        blocks: processedBlocks,
      };
    }

    return block;
  }

  /**
   * Itera recursivamente sobre um mapa de blocos (id -> bloco), aplicando
   * o handler correto para cada bloco com base em seu `@type`.
   *
   * Três casos possíveis para cada bloco:
   *  1. Existe um handler registrado em `handlers` para esse tipo → aplica e retorna.
   *  2. O tipo é `columnsBlock` sem handler externo → delega para o columnsHandler
   *     criado por esta fábrica.
   *  3. Nenhum caso acima → busca blocos filhos em `data.blocks` ou `blocks`
   *     e recursa para processar a estrutura aninhada (ex: colunas sem @type).
   *
   * @param {Object} blocksMap - mapa { [id]: bloco }
   * @param {*} content - contexto repassado para os handlers
   * @returns {Object} novo mapa com blocos processados
   */
  function traverseMap(blocksMap, content) {
    // Guard: evita erros em Object.keys se o mapa vier nulo, undefined ou não-objeto.
    if (
      !blocksMap ||
      typeof blocksMap !== 'object' ||
      Array.isArray(blocksMap)
    ) {
      return blocksMap;
    }

    let hasChanged = false;
    const newMap = {};

    Object.keys(blocksMap).forEach((id) => {
      const inner = blocksMap[id];
      newMap[id] = inner;

      if (!inner) return;

      const type = inner['@type'];

      // Caso 1: handler externo registrado para este tipo — tem prioridade.
      const handler = handlers[type];
      if (typeof handler === 'function') {
        const processed = handler(inner, content);
        if (processed !== inner) {
          newMap[id] = processed;
          hasChanged = true;
        }
        return;
      }

      // Caso 2: bloco é um `columnsBlock` sem handler externo.
      // Usa o handler desta própria fábrica para processar os filhos.
      // O `return` é obrigatório: sem ele o caso 3 sobrescreveria o resultado.
      if (type === 'columnsBlock') {
        const processed = columnsHandler(inner, content);
        if (processed !== inner) {
          newMap[id] = processed;
          hasChanged = true;
        }
        return;
      }

      // Caso 3: bloco genérico aninhado sem @type (ex: colunas dentro de columnsBlock)
      // que pode conter seus próprios blocos em `data.blocks` ou `blocks`.
      const innerBlocks = inner?.data?.blocks ?? inner?.blocks;
      if (innerBlocks) {
        const fixedInnerBlocks = traverseMap(innerBlocks, content);

        if (fixedInnerBlocks !== innerBlocks) {
          const newInner = { ...inner };

          if (inner.data?.blocks) {
            newInner.data = { ...inner.data, blocks: fixedInnerBlocks };
          } else {
            newInner.blocks = fixedInnerBlocks;
          }

          newMap[id] = newInner;
          hasChanged = true;
        }
      }
    });

    return hasChanged ? newMap : blocksMap;
  }

  return columnsHandler;
};

export default createColumnsHandler;
